<script lang="ts" setup>
  import { computed, onMounted } from 'vue';
  import { toast } from 'vue3-toastify';

  import router from '@/router';
  import { useNotificationStore } from '@/stores/agency/notifications/notifications';
  import { UIcolorVariants } from '@/utils/formatters';
  import { convertUTCToUserDateTime } from '@/utils/helper';

  const isMenu = ref(false);
  const notificationStore = useNotificationStore();

  // Use store getters
  const notifications = computed(() => notificationStore.getLastFiveNotifications);
  const unreadCount = computed(() => notificationStore.getUnreadCount);
  const displayCount = computed(() => (unreadCount.value > 99 ? '99+' : unreadCount.value));

  // Mark all as read
  const markAllAsRead = async () => {
    if (unreadCount.value === 0) {
      toast.warning('All Notifications have been read');

      return;
    }

    try {
      await notificationStore.markAllNotificationsAsRead();
      isMenu.value = false;
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark notifications as read');
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      clock_in: 'tabler-login',
      clock_out: 'tabler-logout',
      shift_request_submitted: 'tabler-calendar-time',
      appointment_cancelled: 'tabler-calendar-x',
      role_permissions_updated: 'tabler-shield-lock',
      user_permissions_updated: 'tabler-shield-check'
    };

    return iconMap[type] || 'tabler-bell';
  };

  const goToNotificationPage = () => {
    isMenu.value = false;
    router.push('/agency/notifications');
  };

  // Fetch notifications on mount
  onMounted(async () => {
    try {
      await notificationStore.fetchNotifications();
      await notificationStore.fetchUnreadNotificationCount();
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  });
</script>

<template>
  <IconBtn id="notification-btn">
    <VBadge color="error" :content="displayCount" class="notification-badge">
      <VIcon size="26" icon="tabler-bell" />
    </VBadge>

    <VMenu v-model="isMenu" activator="parent" width="380px" offset="14px" :close-on-content-click="false">
      <VCard>
        <div class="d-flex align-center justify-space-between px-4 py-2">
          <div class="text-h5 text-dark">Notifications</div>
          <IconBtn
            v-show="notifications.length > 0"
            size="small"
            variant="text"
            color="primary"
            class="rounded"
            @click="markAllAsRead"
          >
            <VIcon icon="tabler-mail-opened" size="24" />
            <VTooltip activator="parent" location="top" text="Mark all as read" />
          </IconBtn>
        </div>

        <VDivider />

        <!-- 👉 Notifications list -->
        <div class="pa-3 overflow-y-auto" style="max-block-size: 500px">
          <template v-for="notification in notifications" :key="notification.id">
            <VCard
              class="mb-2"
              variant="outlined"
              :style="!notification.isRead ? 'background-color: rgba(var(--v-theme-primary), 0.10)' : ''"
              elevation="5"
              @click="goToNotificationPage"
            >
              <VCardText class="d-flex pa-3 gap-3">
                <!-- Avatar -->
                <VAvatar :color="UIcolorVariants()" variant="tonal" size="40">
                  <VIcon
                    :icon="getNotificationIcon(notification.type)"
                    :color="!notification.isRead ? 'primary' : 'grey'"
                    size="20"
                  />
                </VAvatar>

                <!-- Content -->
                <div class="d-flex flex-column flex-grow-1">
                  <div class="d-flex align-start justify-space-between ga-2 mb-1">
                    <span class="text-body-1 text-dark" style="min-inline-size: 0">
                      {{ notification.title }}
                    </span>
                    <VChip
                      v-if="!notification.isRead"
                      size="small"
                      color="primary"
                      variant="flat"
                      class="flex-shrink-0"
                    >
                      New
                    </VChip>
                  </div>

                  <span class="text-body-2 mb-2">
                    {{ notification.message }}
                  </span>

                  <div class="d-flex align-center text-caption">
                    <VIcon icon="tabler-clock" size="12" class="me-1" />
                    <span>{{ convertUTCToUserDateTime(notification.createdAt) }}</span>
                  </div>
                </div>
              </VCardText>
            </VCard>
          </template>

          <VCard v-show="notifications.length === 0" class="text-center py-8" elevation="0" variant="tonal">
            <VCardText>
              <VIcon icon="tabler-bell-off" size="48" color="disabled" class="mb-3" />
              <div class="text-body-2 text-medium-emphasis">No Notification Found!</div>
            </VCardText>
          </VCard>
        </div>

        <VDivider />

        <VCardText>
          <VBtn color="primary" variant="text" block to="/agency/notifications" @click="isMenu = false">
            View More
            <VIcon icon="tabler-arrow-right" size="18" class="ms-1" />
          </VBtn>
        </VCardText>
      </VCard>
    </VMenu>
  </IconBtn>
</template>
